import { useState } from 'react';
import { Lock, Mail, Send, Trash, User } from 'react-feather';

import { Button, Confirm, Select, Typography } from '@/components';
import {
  useAuthUser,
  useRoles,
  useTeamMemberInvite,
  useTeamMemberRemove,
  useTeamMemberUpdate
} from '@/hooks';
import { useTranslation } from 'react-i18next';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './TeamMember.module.css';

interface TeamMemberProps {
  setMembers: React.Dispatch<React.SetStateAction<Api.Member[]>>;
  member: Api.Member;
}

export function TeamMember({ member, setMembers }: TeamMemberProps) {
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [memberRole, setMemberRole] = useState('');
  const { t } = useTranslation();
  const user = useAuthUser();
  const ROLES = useRoles(t);

  const { mutation: teamMemberRemove, isLoading: isRemovingMember } = useTeamMemberRemove({
    onSuccess: () => {
      setMembers((prevState) => [
        ...prevState.filter((prevMember) => prevMember?.user_id !== member?.user_id)
      ]);
    },
    teamId: user?.team?.team_id || '',
    userId: member?.user_id
  });

  const { mutation: teamMemberUpdate, isLoading: isUpdatingMember } = useTeamMemberUpdate({
    onSuccess: () => {
      setMembers((prevState) => [
        ...prevState.filter((prevMember) => prevMember?.user_id !== member?.user_id),
        ...prevState
          .filter((prevMember) => prevMember?.user_id === member?.user_id)
          .map((prevMemberNew) => ({
            ...prevMemberNew,
            role: memberRole
          }))
      ]);
      setMemberRole('');
    },
    teamId: user?.team?.team_id || '',
    userId: member?.user_id,
    role: memberRole
  });

  const { mutation: teamMemberReminder, isLoading: isRemindingMember } = useTeamMemberInvite({
    onSuccess: (data: Api.Member) => {
      if (data.status === 'MEMBER') {
        setMembers((prevState) => [
          ...prevState.filter((prevMember) => prevMember?.user_id !== member?.user_id),
          ...prevState
            .filter((prevMember) => prevMember?.user_id === member?.user_id)
            .map((prevMemberNew) => ({
              ...prevMemberNew,
              ...data
            }))
        ]);
      }
    },
    teamId: user?.team?.team_id || '',
    email: member.email,
    role: member.role,
    name: member.name
  });

  const handleRemove = () => {
    setRemoveConfirm(false);
    teamMemberRemove();
  };

  const handleUpdate = (event: TypeAny) => {
    setMemberRole(event.target.value);
    teamMemberUpdate();
  };

  const handleReminder = () => {
    teamMemberReminder();
  };

  const isMember = member.status === 'MEMBER';
  const hasOldInvite =
    (new Date().getTime() - new Date(member.date_invited || new Date()).getTime()) / 1000 > 86400;

  const MailIcon = isMember ? Mail : Send;
  const ICON_PROPS = {
    className: styles.teamMemberColumnIcon,
    size: 16
  };

  const isAdmin = user.team?.role === 'ADMIN';

  return (
    <>
      {removeConfirm && (
        <Confirm handleCancel={() => setRemoveConfirm(false)} handleConfirm={handleRemove}>
          {t('teamMember.confirmRemove')}
        </Confirm>
      )}

      <div className={styles.teamMember}>
        <span className={styles.teamMemberColumn}>
          <User {...ICON_PROPS} />
          {member.name}
        </span>

        <span className={styles.teamMemberColumn} data-type="email">
          <div className={styles.teamMemberColumn}>
            <MailIcon {...ICON_PROPS} />
            <TranslatedLink href={`mailto:${member.email}`}>{member.email}</TranslatedLink>
          </div>

          {!isMember && (
            <span className={styles.teamMemberColumnPending}>
              {hasOldInvite && isAdmin ? (
                <Button isLoading={isRemindingMember} onClick={handleReminder} size="small">
                  {t('teamMember.remind')}
                </Button>
              ) : (
                <Typography sizes={['label']}>{t('teamMember.pending')}</Typography>
              )}
            </span>
          )}
        </span>

        <span className={styles.teamMemberColumn} data-type="role">
          <span className={styles.teamMemberColumnRole}>
            <Lock {...ICON_PROPS} />

            {isAdmin ? (
              <Select
                disabled={isUpdatingMember || isRemovingMember}
                onChange={handleUpdate}
                selected={member.role}
                options={ROLES}
                name="role"
                required
              />
            ) : (
              ROLES.find((role) => role.value === member.role)?.display
            )}

            {isAdmin && member?.user_id !== user?.user_id && (
              <button
                className={styles.teamMemberColumnRemove}
                onClick={() => setRemoveConfirm(true)}
                type="button"
              >
                <Trash {...ICON_PROPS} />
              </button>
            )}
          </span>
        </span>
      </div>
    </>
  );
}
